import { useCallback, useEffect, useState } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { LoadingState } from '@/components/ui/LoadingState';
import { RotateCcw } from 'lucide-react';
import OrganizationCombobox from './OrganizationCombobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Organization {
  id: string;
  name: string;
}

interface AgentRunRow {
  id: string;
  type: string;
  status: string;
  trigger?: string | null;
  attempt: number;
  organization_id: string;
  organization_name?: string | null;
  conversation_id?: string | null;
  account_id?: string | null;
  error_type?: string | null;
  error_message?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  total_tokens?: number | null;
  created_at?: string | null;
  completed_at?: string | null;
}

interface AgentRunDetails extends AgentRunRow {
  agent_id?: string | null;
  visitor_id?: string | null;
  lead_id?: string | null;
  parent_run_id?: string | null;
  root_run_id?: string | null;
  input?: unknown;
  output?: unknown;
  error?: unknown;
  metadata?: unknown;
  idempotency_key?: string | null;
  queued_at?: string | null;
  started_at?: string | null;
  updated_at?: string | null;
}

const STATUS_OPTIONS = ['failed', 'pending', 'running', 'succeeded', 'skipped', 'all'];

const STATUS_BADGE: Record<string, string> = {
  failed: 'bg-red-500/10 text-red-700 dark:text-red-400',
  pending: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  running: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  succeeded: 'bg-green-500/10 text-green-700 dark:text-green-400',
  skipped: 'bg-muted text-muted-foreground',
};

export default function AgentRunsTab() {
  const { get, post } = useApi();
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState<AgentRunRow[]>([]);
  const [status, setStatus] = useState('failed');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationId, setOrganizationId] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeRun, setActiveRun] = useState<AgentRunDetails | null>(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, page: String(page), per_page: '50' });
      if (organizationId && organizationId !== 'all') params.set('organization_id', organizationId);
      const response = await get(`/admin/agent-runs?${params.toString()}`);
      setRuns(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
      setSelected(new Set());
    } catch (error) {
      console.error('Error fetching agent runs:', error);
      toast.error('Failed to load agent runs');
    } finally {
      setLoading(false);
    }
  }, [get, status, organizationId, page]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await get('/admin/organizations/');
        setOrganizations(response.data || []);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, []);

  const failedOnPage = runs.filter((run) => run.status === 'failed');
  const allFailedSelected =
    failedOnPage.length > 0 && failedOnPage.every((run) => selected.has(run.id));

  const toggleAll = () => {
    if (allFailedSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(failedOnPage.map((run) => run.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const retrySelected = async () => {
    if (selected.size === 0) return;
    setRetrying(true);
    try {
      const response = await post('/admin/agent-runs/retry', { ids: Array.from(selected) });
      const { retried, skipped = [], enqueue_failures = [] } = response.data || {};
      if (retried) toast.success(`Re-enqueued ${retried} run${retried === 1 ? '' : 's'}`);
      if (skipped.length) toast.info(`${skipped.length} not retried (no longer failed)`);
      if (enqueue_failures.length) toast.error(`${enqueue_failures.length} failed to enqueue`);
      await fetchRuns();
    } catch (error) {
      console.error('Error retrying agent runs:', error);
      toast.error('Failed to retry agent runs');
    } finally {
      setRetrying(false);
    }
  };

  const openRunDetails = async (run: AgentRunRow) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setActiveRun({ ...run });
    try {
      const response = await get(`/admin/agent-runs/${run.id}`);
      setActiveRun(response.data);
    } catch (error) {
      console.error('Error fetching agent run details:', error);
      toast.error('Failed to load agent run details');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <TabsContent value="agent-runs">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={status} onValueChange={(value) => { setPage(1); setStatus(value); }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option === 'all' ? 'All statuses' : option.charAt(0).toUpperCase() + option.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <OrganizationCombobox
            organizations={organizations}
            value={organizationId}
            onChange={(value: string) => { setPage(1); setOrganizationId(value); }}
          />
          <span className="text-sm text-muted-foreground">{total} run{total === 1 ? '' : 's'}</span>
          <div className="ml-auto">
            <Button
              onClick={retrySelected}
              disabled={selected.size === 0 || retrying}
              size="sm"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              {retrying ? 'Retrying…' : `Retry ${selected.size ? selected.size : 'selected'}`}
            </Button>
          </div>
        </div>

        {loading ? (
          <LoadingState label="Loading agent runs..." className="min-h-[30vh]" />
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allFailedSelected}
                      onCheckedChange={toggleAll}
                      disabled={failedOnPage.length === 0}
                      aria-label="Select all failed runs on page"
                    />
                  </TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempt</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.length ? runs.map((run) => (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer"
                    onClick={() => openRunDetails(run)}
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      {run.status === 'failed' && (
                        <Checkbox
                          checked={selected.has(run.id)}
                          onCheckedChange={() => toggleOne(run.id)}
                          aria-label={`Select run ${run.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {run.created_at ? formatDate(run.created_at) : '-'}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate" title={run.organization_name || run.organization_id}>
                      {run.organization_name || run.organization_id}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{run.type}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {run.trigger || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`border-0 ${STATUS_BADGE[run.status] || ''}`}>
                        {run.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular-nums">{run.attempt}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {typeof run.total_tokens === 'number' ? run.total_tokens.toLocaleString() : '-'}
                    </TableCell>
                    <TableCell className="max-w-[320px]">
                      {run.error_type ? (
                        <span className="text-sm" title={run.error_message || undefined}>
                          <span className="font-medium">{run.error_type}</span>
                          {run.error_message && (
                            <span className="text-muted-foreground"> — {run.error_message.slice(0, 120)}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9} className="h-20 text-center text-muted-foreground">
                      No {status === 'all' ? '' : `${status} `}runs found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agent Run Details</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {activeRun?.id || 'Loading run…'}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <LoadingState label="Loading run details..." className="min-h-48" />
          ) : activeRun ? (
            <div className="space-y-6">
              <div className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {[
                  ['Organization', activeRun.organization_name || activeRun.organization_id],
                  ['Type', activeRun.type],
                  ['Status', activeRun.status],
                  ['Trigger', activeRun.trigger],
                  ['Attempt', activeRun.attempt],
                  ['Tokens', activeRun.total_tokens?.toLocaleString()],
                  ['Input Tokens', activeRun.input_tokens?.toLocaleString()],
                  ['Output Tokens', activeRun.output_tokens?.toLocaleString()],
                  ['Created', activeRun.created_at ? formatDate(activeRun.created_at) : null],
                  ['Queued', activeRun.queued_at ? formatDate(activeRun.queued_at) : null],
                  ['Started', activeRun.started_at ? formatDate(activeRun.started_at) : null],
                  ['Completed', activeRun.completed_at ? formatDate(activeRun.completed_at) : null],
                  ['Conversation ID', activeRun.conversation_id],
                  ['Account ID', activeRun.account_id],
                  ['Lead ID', activeRun.lead_id],
                  ['Visitor ID', activeRun.visitor_id],
                  ['Agent ID', activeRun.agent_id],
                  ['Parent Run ID', activeRun.parent_run_id],
                  ['Root Run ID', activeRun.root_run_id],
                  ['Idempotency Key', activeRun.idempotency_key],
                ].map(([label, value]) => (
                  <div key={String(label)} className="min-w-0">
                    <div className="text-xs font-medium text-muted-foreground">{label}</div>
                    <div className="break-all">{value ?? '-'}</div>
                  </div>
                ))}
              </div>
              {[
                ['Input', activeRun.input],
                ['Output', activeRun.output],
                ['Error', activeRun.error],
                ['Metadata', activeRun.metadata],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <h3 className="mb-2 text-sm font-semibold">{String(label)}</h3>
                  <pre className="max-h-80 overflow-auto rounded-lg bg-muted p-4 text-xs whitespace-pre-wrap break-words">
                    {value == null ? '-' : JSON.stringify(value, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Run details could not be loaded.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
